/**
 * TeamSlider - Handles team selection functionality for both mobile and desktop
 * Manages team filtering, modal interactions, and responsive behavior
 */
class TeamSlider {
  constructor() {
    // Initialize properties
    this.isMobile = this.checkIfMobile();
    this.selectedTeam = null;
    this.elements = {
      modal: null,
      selectedDisplay: null,
      cancelBtn: null,
      cancelBtnCopy: null,
      applyBtn: null
    };
    this.isInitialized = false;

    // Initialize, set up event listeners and clear localStorage on page load
    this.clearLocalStorage();

    this.init();
    this.setupResizeListener();
  }

  /**
   * Check if current viewport is mobile
   * @returns {boolean} True if mobile viewport
   */
  checkIfMobile() {
    return window.innerWidth <= 768;
  }

  /**
   * Initialize the component based on current viewport
   */
  init() {
    if (this.isInitialized) return;

    if (this.isMobile) {
      this.initMobile();
    } else {
      this.initDesktop();
    }

    this.isInitialized = true;
  }

  /**
   * Set up responsive behavior with resize listener
   */
  setupResizeListener() {
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = this.checkIfMobile();

      // Only reinitialize if viewport type changed
      if (wasMobile !== this.isMobile) {
        this.cleanup();
        this.isInitialized = false;
        this.init();
      }
    });
  }

  cleanup() {
    if (this.isMobile) {
      // Clean up old desktop listeners
      this.cleanupDesktopListeners();
    } else {
      // Clean up old mobile listeners
      this.cleanupMobileListeners();
    }
  }

  cleanupDesktopListeners() {
    const desktopTeamItems = document.querySelectorAll('.team-slider__item');
    desktopTeamItems.forEach(item => {
      // Clone node to remove all event listeners
      const newItem = item.cloneNode(true);
      item.parentNode.replaceChild(newItem, item);
    });
  }

  cleanupMobileListeners() {
    if (this.elements.selectedDisplay) {
      const newDisplay = this.elements.selectedDisplay.cloneNode(true);
      this.elements.selectedDisplay.parentNode.replaceChild(newDisplay, this.elements.selectedDisplay);
    }
  }

  /**
   * Initialize mobile-specific functionality
   */
  initMobile() {
    this.cacheMobileElements();

    if (!this.validateMobileElements()) {
      console.warn('Team slider mobile elements not found');
      return;
    }

    this.setupMobileEventListeners();
    this.setInitialState();

    // Update mobile display to reflect any restored team selection
    this.updateMobileToggleDisplay();
  }

  /**
   * Cache mobile DOM elements for better performance
   */
  cacheMobileElements() {
    this.elements.modal = document.getElementById('teamModal');
    this.elements.selectedDisplay = document.getElementById('selectedTeamDisplay');
    this.elements.cancelBtn = document.getElementById('cancelTeam');
    this.elements.cancelBtnCopy = document.getElementById('cancelTeamCopy');
    this.elements.applyBtn = document.getElementById('applyTeam');
  }

  /**
   * Validate that required mobile elements exist
   * @returns {boolean} True if all required elements are found
   */
  validateMobileElements() {
    return this.elements.modal && this.elements.selectedDisplay;
  }

  /**
   * Set up mobile event listeners
   */
  setupMobileEventListeners() {
    // Main interaction listeners
    this.elements.selectedDisplay.addEventListener('click', this.openModal.bind(this));
    this.elements.cancelBtn.addEventListener('click', this.cancelSelection.bind(this));
    this.elements.cancelBtnCopy.addEventListener('click', this.clearFilters.bind(this));
    this.elements.applyBtn.addEventListener('click', this.applySelection.bind(this));

    // Modal overlay click to close
    const overlay = this.elements.modal.querySelector('.team-slider__mobile-overlay');
    if (overlay) {
      overlay.addEventListener('click', this.cancelSelection.bind(this));
    }

    // Team item selection listeners
    this.setupTeamItemListeners();
  }

  /**
   * Set up click listeners for team items in modal
   */
  setupTeamItemListeners() {
    const teamItems = this.elements.modal.querySelectorAll('.team-slider__item');
    teamItems.forEach(item => {
      item.addEventListener('click', () => this.selectTeam(item));
    });
  }

  /**
   * Initialize desktop-specific functionality
   */
  initDesktop() {
    const desktopTeamItems = document.querySelectorAll('.team-slider__item');

    desktopTeamItems.forEach(item => {
      item.addEventListener('click', () => {
        this.selectTeam(item);

        // Handle selection immediately for desktop
        if (this.selectedTeam) {
          this.handleTeamSelection(item);
        }
      });
    });

    this.setInitialState();
  }

  /**
   * Set initial state and restore from localStorage if available
   */
  setInitialState() {
    this.selectedTeam = null;

    const savedTeam = localStorage.getItem('selectedTeam');
    if (this.isValidSavedTeam(savedTeam)) {
      this.restoreSavedTeam(savedTeam);
    }
  }

  /**
   * Check if saved team is valid
   * @param {string} savedTeam - Team name from localStorage
   * @returns {boolean} True if team is valid
   */
  isValidSavedTeam(savedTeam) {
    return savedTeam && savedTeam !== 'NHL TEAM';
  }

  /**
   * Restore previously selected team from localStorage
   * @param {string} savedTeam - Team name to restore
   */
  restoreSavedTeam(savedTeam) {
    const teamItems = document.querySelectorAll('.team-slider__item');

    teamItems.forEach(item => {
      const teamName = this.getTeamName(item);
      if (teamName === savedTeam) {
        this.selectTeam(item);
        this.handleTeamSelection(item);
      }
    });
  }

  /**
   * Get team name from team item element
   * @param {HTMLElement} teamItem - Team item element
   * @returns {string} Team name
   */
  getTeamName(teamItem) {
    const nameSelector = this.isMobile ? '.team-slider__mobile-name' : '.team-slider__name';
    return teamItem.querySelector(nameSelector)?.textContent || '';
  }

  /**
   * Open the team selection modal
   */
  openModal() {
    this.elements.modal.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.showAllTeams();
  }

  /**
   * Close the team selection modal
   */
  closeModal() {
    this.elements.modal.classList.remove('open');
    document.body.style.overflow = '';
  }

  /**
   * Show all teams in the modal dropdown
   */
  showAllTeams() {
    const teamItems = this.elements.modal.querySelectorAll('.team-slider__item');
    teamItems.forEach(item => {
      item.style.display = 'flex';
    });
  }

  /**
   * Handle team selection/deselection
   * @param {HTMLElement} teamItem - Selected team item
   */
  selectTeam(teamItem) {
    // Toggle selection if same team clicked
    if (this.selectedTeam === teamItem) {
      this.deselectTeam();
      return;
    }

    this.selectedTeam = teamItem;
    this.updateTeamSelectionUI(teamItem);
    this.handleNHLToggles();
    this.addB2BGridWrapper();
  }

  /**
   * Update UI to reflect team selection
   * @param {HTMLElement} teamItem - Selected team item
   */
  updateTeamSelectionUI(teamItem) {
    // Clear all selections
    document.querySelectorAll('.team-slider__item').forEach(item => {
      item.classList.remove('selected');
    });

    // Add selection to clicked item
    teamItem.classList.add('selected');
  }

  /**
   * Handle NHL collection toggles when team is selected
   */
  handleNHLToggles() {
    const nhlToggleItems = document.querySelectorAll('.nhl-collection-toggles__item');
    const nhlToggles = document.querySelector('nhl-collection-toggles');

    nhlToggleItems.forEach(item => {
      item.classList.remove('active');
      if (nhlToggles && typeof nhlToggles.showSection === 'function') {
        nhlToggles.showSection(item.dataset.section, false, true);
      }
    });
  }

  /**
   * Deselect current team and reset state
   */
  deselectTeam() {
    this.selectedTeam = null;
    this.clearTeamSelectionUI();
    this.clearLocalStorage();
    this.filterProductsByTeam('');
    this.addB2BGridWrapper();
    this.resetNHLToggles();
  }

  /**
   * Clear team selection UI
   */
  clearTeamSelectionUI() {
    document.querySelectorAll('.team-slider__item').forEach(item => {
      item.classList.remove('selected');
    });
  }

  /**
   * Clear team selection from localStorage
   */
  clearLocalStorage() {
    localStorage.removeItem('selectedTeam');
  }

  /**
   * Reset NHL toggles to default state
   */
  resetNHLToggles() {
    const nhlToggleItems = document.querySelectorAll('.nhl-collection-toggles__item');
    const nhlToggles = document.querySelector('nhl-collection-toggles');

    this.removeB2BGridWrapper();

    if (nhlToggleItems.length > 0) {
      // Select first NHL toggle
      nhlToggleItems.forEach(item => item.classList.remove('active'));
      const firstToggle = nhlToggleItems[0];
      firstToggle.classList.add('active');

      // Show first section
      if (nhlToggles && typeof nhlToggles.showSection === 'function') {
        nhlToggles.hideSection();
        nhlToggles.showSection(firstToggle.dataset.section);
      }
    }
  }

  /**
   * Update mobile toggle display with selected team or default NHL state
   */
  updateMobileToggleDisplay() {
    const selectedLogo = this.elements.selectedDisplay.querySelector('.team-slider__mobile-selected-logo');
    const selectedName = this.elements.selectedDisplay.querySelector('.team-slider__mobile-selected-name');

    if (!selectedLogo || !selectedName) return;

    if (this.selectedTeam) {
      this.updateDisplayWithTeam(selectedLogo, selectedName);
      this.elements.selectedDisplay.classList.add('active');
      this.elements.cancelBtnCopy.style.display = 'block';
    } else {
      this.updateDisplayWithDefault(selectedLogo, selectedName);
      this.elements.selectedDisplay.classList.remove('active');
      this.elements.cancelBtnCopy.style.display = 'none';
    }
  }

  /**
   * Update display with selected team information
   * @param {HTMLElement} logo - Logo element
   * @param {HTMLElement} name - Name element
   */
  updateDisplayWithTeam(logo, name) {
    const image = this.selectedTeam.querySelector('.team-slider__mobile-logo');
    const teamName = this.selectedTeam.querySelector('.team-slider__mobile-name')?.textContent;

    if (image && teamName) {
      logo.src = image.src;
      logo.alt = image.alt;
      name.textContent = teamName;
    }
  }

  /**
   * Update display with default NHL state
   * @param {HTMLElement} logo - Logo element
   * @param {HTMLElement} name - Name element
   */
  updateDisplayWithDefault(logo, name) {
    const NHL_LOGO_URL = 'https://cdn.shopify.com/s/files/1/0932/7770/files/NHL_Logo.png?v=1738000328';

    logo.src = NHL_LOGO_URL;
    logo.alt = 'NHL Logo';
    name.textContent = 'NHL TEAM';
  }

  /**
   * Reset selection in modal
   */
  resetSelection() {
    this.elements.modal.querySelectorAll('.team-slider__item').forEach(item => {
      item.classList.remove('selected');
    });
    this.selectedTeam = null;
  }

  /**
   * Apply current selection and close modal
   */
  applySelection() {
    this.updateMobileToggleDisplay();

    if (this.selectedTeam) {
      this.handleTeamSelection(this.selectedTeam);
    } else {
      this.deselectTeam();
    }

    this.closeModal();
  }

  /**
   * Cancel selection and close modal
   */
  cancelSelection() {
    this.resetSelection();
    this.closeModal();
  }

  /**
   * Clear all filters and reset state
   */
  clearFilters() {
    this.deselectTeam();
    this.updateMobileToggleDisplay()
    this.resetSelection();
    this.elements.cancelBtnCopy.style.display = 'none';
  }

  /**
   * Handle team selection logic
   * @param {HTMLElement} teamItem - Selected team item
   */
  handleTeamSelection(teamItem) {
    const teamName = this.getTeamName(teamItem);

    localStorage.setItem('selectedTeam', teamName);
    this.filterProductsByTeam(teamName);
  }

  /**
   * Filter products by selected team
   * @param {string} teamName - Team name to filter by
   */
  filterProductsByTeam(teamName) {
    const productGrids = document.querySelectorAll('[id^="product-grid"]');

    if (productGrids.length === 0) {
      console.warn('No product grids found on the page');
      return;
    }

    productGrids.forEach((grid, index) => {
      this.filterSingleProductGrid(grid, teamName, index);
    });
  }

  /**
   * Filter products in a single product grid
   * @param {HTMLElement} productGrid - Product grid element
   * @param {string} teamName - Team name to filter by
   * @param {number} gridIndex - Grid index for logging
   */
  filterSingleProductGrid(productGrid, teamName, gridIndex) {
    const productCards = productGrid.querySelectorAll('.product-card');

    if (productCards.length === 0) {
      return;
    }

    productCards.forEach(card => {
      this.filterProductCard(card, teamName);
    });
  }

  /**
   * Filter individual product card
   * @param {HTMLElement} card - Product card element
   * @param {string} teamName - Team name to filter by
   */
  filterProductCard(card, teamName) {
    const productTeam = card.dataset.team;
    const listItem = card.closest('li');

    if (this.shouldShowProduct(teamName, productTeam)) {
      this.showProductCard(card, listItem, teamName);
    } else {
      this.hideProductCard(card, listItem);
    }
  }

  /**
   * Determine if product should be shown based on team filter
   * @param {string} teamName - Selected team name
   * @param {string} productTeam - Product's team
   * @returns {boolean} True if product should be shown
   */
  shouldShowProduct(teamName, productTeam) {
    if (!teamName || teamName === '' || teamName === 'NHL TEAM') {
      return true; // Show all products when no team selected
    }

    return productTeam && productTeam.toLowerCase().includes(teamName.toLowerCase());
  }

  /**
   * Show product card with appropriate styling
   * @param {HTMLElement} card - Product card element
   * @param {HTMLElement} listItem - List item container
   * @param {string} teamName - Selected team name
   */
  showProductCard(card, listItem, teamName) {
    card.style.display = 'block';

    if (listItem) {
      listItem.style.display = 'block';

      if (teamName && teamName !== 'NHL TEAM') {
        // Apply special styling for team-filtered products
        listItem.style.maxWidth = '100%';
        listItem.style.flex = '1 1 100%';
        listItem.style.padding = '0';
      } else {
        // Reset styles for unfiltered products
        listItem.style.maxWidth = '';
        listItem.style.flex = '';
      }
    }
  }

  /**
   * Hide product card
   * @param {HTMLElement} card - Product card element
   * @param {HTMLElement} listItem - List item container
   */
  hideProductCard(card, listItem) {
    card.style.display = 'none';
    if (listItem) {
      listItem.style.display = 'none';
    }
  }

  /**
   * Manage B2B grid wrapper for side-by-side layout
   */
  addB2BGridWrapper() {
    const b2bSections = document.querySelectorAll('.section--b2b-collection-product-grid');
    const hasSelectedTeam = this.selectedTeam !== null;

    if (hasSelectedTeam && b2bSections.length > 1) {
      this.createB2BGridWrapper(b2bSections);
    } else {
      this.removeB2BGridWrapper();
    }
  }

  /**
   * Create and configure B2B grid wrapper
   * @param {NodeList} b2bSections - B2B collection sections
   */
  createB2BGridWrapper(b2bSections) {
    let wrapper = document.querySelector('.b2b-grids-wrapper');

    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'b2b-grids-wrapper';

      const firstSection = b2bSections[0];
      firstSection.parentNode.insertBefore(wrapper, firstSection);
    }

    // Move sections to wrapper and apply styling
    b2bSections.forEach(section => {
      if (section.parentNode !== wrapper) {
        wrapper.appendChild(section);
      }

      section.classList.add('b2b-grid-item');
    });
  }

  /**
   * Remove B2B grid wrapper and restore original layout
   */
  removeB2BGridWrapper() {
    const existingWrapper = document.querySelector('.b2b-grids-wrapper');

    if (existingWrapper) {
      const sections = existingWrapper.querySelectorAll('.section--b2b-collection-product-grid');

      sections.forEach(section => {
        section.style.display = 'block';
        existingWrapper.parentNode.insertBefore(section, existingWrapper);
      });

      existingWrapper.remove();
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new TeamSlider();
});